import {
  type AccountInfo,
  type Configuration,
  type IPublicClientApplication,
  PublicClientApplication,
} from "@azure/msal-browser";
import { env } from "@/lib/env";

// The portal AAD app is a multi-tenant SPA registration. Customer admins
// sign in against their own M365 tenant, so the authority is the
// `/organizations` multi-tenant endpoint, not a fixed tenant.
const msalConfig: Configuration = {
  auth: {
    clientId: env.msalClientId,
    authority: env.msalAuthority,
    // Registered redirect URI of the portal AAD app — the SPA root.
    redirectUri: window.location.origin + "/",
    postLogoutRedirectUri: window.location.origin + "/",
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false,
  },
};

export const msalInstance = new PublicClientApplication(msalConfig);

// Only the OIDC scopes — no Microsoft Graph data scopes. The portal
// backend verifies the resulting ID token; `tid` + email are all we need.
export const loginRequest = { scopes: ["openid", "profile", "email"] };

/**
 * Acquire the customer's Microsoft ID token (a raw JWT) to send as the
 * Bearer credential to the portal API. Tries the silent cache first and
 * falls back to an interactive redirect if the cache is cold.
 */
export async function acquireIdToken(
  instance: IPublicClientApplication,
  account: AccountInfo,
): Promise<string> {
  const result = await instance.acquireTokenSilent({
    ...loginRequest,
    account,
  });
  if (!result.idToken) {
    // Cache miss / no ID token — bounce through an interactive sign-in.
    await instance.acquireTokenRedirect({ ...loginRequest, account });
    throw new Error("redirecting for interactive sign-in");
  }
  return result.idToken;
}
